<template>
  <div class="screen-size">
    <v-card class="d-flex pa-1 bg" outlined>
      <div class="mr-auto d-flex">
        <system-linked-combo v-model="selectedSystem" :hideStack="true" class="pr-2" @input="fetchHistories"></system-linked-combo>
      </div>
      <div>
        <v-btn @click="collectRoleData" class="mr-2" x-small>
          <v-icon small left>mdi-database-import</v-icon>
          {{ this.$t('rtrck.collect') }}
        </v-btn>
        <v-btn @click="stopCollecting" x-small :disabled="disableStop">
          <v-icon small left>mdi-stop-circle</v-icon>
          {{ this.$t('rtrck.stop') }}
        </v-btn>
        <v-btn @click="deleteRoleData" x-small :disabled="disableDelete">
          <v-icon small left>mdi-database-minus</v-icon>
          {{ this.$t('rtrck.delete') }}
        </v-btn>
        <span class="mx-3">|</span>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" @click="paging = { page: 1, rowsPerPage: paging.rowsPerPage }" color="primary" x-small>
              <v-icon small>mdi-magnify</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('common.retrieve') }}</span>
        </v-tooltip>
      </div>
    </v-card>
    <v-data-table v-model="selectedHistories" :headers="headers" :items="histories" item-key="sequence" :options.sync="options" :items-per-page="paging.rowsPerPage" :loading="loading" :height="height" class="elevation-1 content" must-sort show-select single-select fixed-header dense hide-default-footer>
      <template v-slot:item.serverYmdhms="{ item }"> {{ item.serverYmdhms | format('yyyy-MM-dd HH:mm:ss') }} </template>
      <template v-slot:item.type="{ item }"> {{ $t(`rtrck.exec.type.${item.type}`) }} </template>
      <template v-slot:item.status="{ item }"> {{ $t(`cmmn.exec.status.${item.status}`) }} </template>
      <template v-slot:item.executedYmdhms="{ item }"> {{ item.executedYmdhms | format('yyyy-MM-dd HH:mm:ss') }} </template>
      <template v-slot:item.executedRate="{ item }"> {{ item.executedRate | comma }} % </template>
      <template v-slot:item.exceptionMessage="{ item }"> {{ item.exceptionMessage }} </template>
      <template v-slot:footer>
        <pagination :paging.sync="paging" :length="length" :disabled="loading"></pagination>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { api } from '@/api/common/instances';
import SystemLinkedCombo from '@/components/SystemLinkedCombo.vue';

export default {
  components: {
    SystemLinkedCombo,
  },
  data() {
    return {
      histories: [],
      options: {},
      length: 0,
      paging: { page: 1, rowsPerPage: 50 },
      loading: false,
      progress: false,
      height: 530,
      selectedSystem: { stack: 'ABAP', system: '' },
      selectedHistories: [],
    };
  },
  watch: {
    paging: {
      deep: true,
      handler() {
        this.fetchHistories();
      },
    },
  },
  computed: {
    disableStop() {
      if (this.selectedHistories.length == 0) return true;
      let elm = this.selectedHistories[0];
      return elm.type != '1' || (elm.type === '1' && elm.status != '0');
    },
    disableDelete() {
      if (this.selectedHistories.length == 0) return true;
      return this.selectedHistories[0].status === '0';
    },
    headers() {
      return [
        { text: this.$t('rtrck.collect.datetime'), value: 'serverYmdhms', sortable: false, width: '12%', align: 'center' },
        { text: this.$t('rtrck.collect.type'), value: 'type', sortable: false, width: '6%', align: 'center' },
        { text: this.$t('rtrck.collect.status'), value: 'status', sortable: false, width: '8%', align: 'center' },
        { text: this.$t('rtrck.finish.datetime'), value: 'executedYmdhms', sortable: false, width: '12%', align: 'center' },
        { text: this.$t('rtrck.collect.rate'), value: 'executedRate', sortable: false, width: '8%', align: 'end' },
        { text: this.$t('rtrck.ex.msg'), value: 'exceptionMessage', sortable: false },
      ];
    },
  },
  methods: {
    async fetchHistories() {
      this.startLoading();
      // eslint-disable-next-line prettier/prettier
      const { data: { items, pagination } } = await api.get('/rtrck/collect/histories', {
        params: {
          sstmId: this.selectedSystem.system,
          page: this.paging.page,
          limit: this.paging.rowsPerPage,
        },
      });
      this.histories = items;
      this.length = pagination.totalCount;
      this.finishLoading();
    },
    async collectRoleData() {
      const { data } = await api.get('/rtrck/collect/is-running', {
        params: {
          sstmId: this.selectedSystem.system,
          type: '1', // UPDATE
        },
      });
      if (data) {
        this.$snackbar(this.$t('rtrck.msg.is.running')); // 이미 수집 중입니다.
        return;
      }
      let confirmRslt = await this.$dialog.confirm(this.$t('rtrck.confirm.collect'));
      if (confirmRslt) {
        const param = new URLSearchParams();
        param.append('sstmId', this.selectedSystem.system);
        const { status } = await api.post('/rtrck/collect/update-all', param);
        if (status == 200) {
          setTimeout(() => {
            this.fetchHistories();
          }, 500);
        }
      }
    },
    async stopCollecting() {
      const param = new URLSearchParams();
      param.append('sequence', this.selectedHistories[0].sequence);
      const { status } = await api.post('/rtrck/collect/stop', param);
      if (status == 200) {
        this.$snackbar(this.$t('rtrck.msg.stopped'));
        this.fetchHistories();
      }
    },
    async deleteRoleData() {
      const { data } = await api.get('/rtrck/collect/is-running', {
        params: {
          sstmId: this.selectedSystem.system,
          type: '2', // DELETE
        },
      });
      if (data) {
        this.$snackbar(this.$t('rtrck.msg.is.erasing')); // 이미 삭제 중입니다.
        return;
      }
      let confirmRslt = await this.$dialog.confirm(this.$t('rtrck.confirm.delete'));
      if (confirmRslt) {
        const param = new URLSearchParams();
        param.append('sequence', this.selectedHistories[0].sequence);
        const { status } = await api.post('/rtrck/collect/delete-all', param);
        if (status == 200) {
          this.fetchHistories();
        }
      }
    },
    startLoading() {
      this.loading = true;
    },
    finishLoading() {
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.screen-size {
  width: 1200px;
  height: 600px;
}
.v-input--selection-controls {
  margin: 0 0 0 3px;
  padding: 0;
}
.v-data-table {
  border-radius: 0px;
}
</style>
